import {
  IProjectLoanType,
  IYieldCompounding,
} from '~/@api/generated-api-types-project/Api'
import { ITranslatedText } from '~/@pages_investing/projects/_project_types/project.types'
import { formatAmount } from '~/@lib/utils/number.utils'
import { formatDate } from '~/@lib/utils/date.utils'
import { nonNullable } from '~/@lib/utils/type.utils'
import { Text } from '~/@mid_ui/typography/Text'
import type { ProjectSchema } from '~/@pages_investing/projects/_project_types/project.types'
import { projectTooltipByType } from '~/@pages_investing/projects/_projects_ui/project-info-tooltips/projectTooltipByType'
import { getProjectLoanTypeLabel } from '~/@pages_investing/projects/_projects_ui/project-loan-type/labelByLoanType'
import { getProjectCollateralSubcategoryLabel } from '~/@pages_investing/projects/_projects_ui/project-collateral/labelByCollateralSubcategory'
import {
  DetailItem,
  type DetailItemRow,
} from '../project-detail_ui/detail-item/DetailItem'
import { DetailHeading } from '../project-detail_ui/detail-heading/DetailHeading'
import { projectDetailTooltipByType } from '../project-detail_ui/detail-tooltips/detailTooltips'
import { useRevenueFee } from '~/@pages_investing/_utils/revenue-fee/useRevenueFee'
import { ProjectPaymentTypeLabel } from '~/@pages_investing/projects/_projects_ui/project-payment-type-label/ProjectPaymentTypeLabel'

type Props = {
  project: ProjectSchema
  lastPaymentDate?: string
}

function ProjectV1Parameters({ project, lastPaymentDate }: Props) {
  const { calculateFeeNettoAmount } = useRevenueFee()

  const items = [
    {
      heading: (
        <DetailHeading
          title="ID:"
          tooltip={projectDetailTooltipByType.businessId}
        />
      ),
      value: <Text>{project.businessId}</Text>,
    },
    {
      heading: <DetailHeading title="Názov projektu:" />,
      value: (
        <Text>
          {
            (project.title as unknown as Record<ITranslatedText, string>)[
              ITranslatedText.Sk
            ]
          }
        </Text>
      ),
    },
    {
      heading: (
        <DetailHeading
          title="Typ zaistenia:"
          tooltip={projectTooltipByType.category}
        />
      ),
      value: (
        <Text>
          {getProjectCollateralSubcategoryLabel(
            project.collateralInfo.collateralSubcategory
          )}
        </Text>
      ),
    },
    {
      heading: (
        <DetailHeading
          title="Typ úveru:"
          tooltip={projectDetailTooltipByType.loanType}
        />
      ),
      value: (
        <Text>
          {getProjectLoanTypeLabel(
            project.loanType || IProjectLoanType.AssetBasedLoan
          )}
        </Text>
      ),
    },
    {
      heading: <DetailHeading title="Celkom preinvestované:" />,
      value: <Text>{formatAmount(project.amountCurrent)}</Text>,
    },
    {
      heading: (
        <DetailHeading
          title="Úrok ako predpokladaný výnos:"
          tooltip={projectTooltipByType.yield}
        />
      ),
      value: (
        <Text className="text-[#3BB200] self-end">
          {calculateFeeNettoAmount(project.scheme.fixedYield).toFixed(2)}% p.a.
        </Text>
      ),
    },
    {
      heading: (
        <DetailHeading
          title="Typ výnosu:"
          tooltip={projectTooltipByType.paymentType}
        />
      ),
      value: (
        <Text>
          <ProjectPaymentTypeLabel
            projectPaymentType={project.paymentTypeId}
            isYieldCompounding={
              project.yieldCompounding === IYieldCompounding.Annually
            }
          />
        </Text>
      ),
    },
    nonNullable(project.investors)
      ? {
          heading: <DetailHeading title="Počet investorov:" />,
          value: <Text>{project.investors}</Text>,
        }
      : null,
    project.startDate && {
      heading: <DetailHeading title="Čas pridania investície:" />,
      value: <Text>{formatDate(project.startDate)}</Text>,
    },
    lastPaymentDate && {
      heading: <DetailHeading title="Čas splatenia investície:" />,
      value: <Text>{formatDate(lastPaymentDate)}</Text>,
    },
    {
      heading: (
        <DetailHeading
          title="Minimálna investícia:"
          tooltip={projectDetailTooltipByType.minimumInvestment}
        />
      ),
      value: <Text>{formatAmount(project.amountMin)}</Text>,
    },
  ]

  return (
    <DetailItem
      borderBottom="none"
      sectionId="investmentParameters"
      title="Parametre investície"
      rows={items.filter(nonNullable) as Array<DetailItemRow>}
    />
  )
}

export { ProjectV1Parameters }
